/**
 * API for the Settings → Prompt admin grid (platform super admins / userType 2).
 *
 * Prompts are stored in the backend `prompt` table and drive every OpenAI call;
 * these helpers read the list and save edits. Mirrors the auth/fetch pattern of
 * the other instructor service modules.
 */

const apiUrl = process.env.REACT_APP_API_URL;

function getBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

async function readJson(res) {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Request failed (${res.status})${body ? `: ${body}` : ''}`);
  }
  return res.json().catch(() => ({}));
}

/** All prompts, grouped by category then key (backend order). */
export async function fetchPrompts() {
  const res = await fetch(`${apiUrl}/api/prompts`, {
    method: 'GET',
    headers: { Accept: '*/*', Authorization: getBasicAuthHeader() },
  });
  const data = await readJson(res);
  return Array.isArray(data) ? data : [];
}

/** Save an edited prompt body. */
export async function updatePrompt(id, content, updatedBy) {
  const res = await fetch(`${apiUrl}/api/prompts/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ content, updatedBy }),
  });
  return readJson(res);
}

/**
 * Renders a prompt from the backend store with the given variables. The template
 * text and {{variable}} interpolation live server-side, so super-admin edits from
 * the Prompt grid take effect on the next request without a code change.
 */
export async function renderPrompt(key, vars = {}) {
  const res = await fetch(`${apiUrl}/api/prompts/render`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ key, vars }),
  });
  const data = await readJson(res);
  return (data && data.prompt) || '';
}

/** Restore a prompt to its in-code default. */
export async function resetPrompt(id, updatedBy) {
  const res = await fetch(`${apiUrl}/api/prompts/${id}/reset`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: '*/*',
      Authorization: getBasicAuthHeader(),
    },
    body: JSON.stringify({ updatedBy }),
  });
  return readJson(res);
}
